import { GameController } from "../../controller/GameController";
import { GameState, GameMessageEvent } from "../../../core/protocol/types";
import RemoteGameManager from "../managers/RemoteGameManager";
import LocalGameManager from "../managers/LocalGameManager";
import {
  MultiplayerGameLoop,
  GameLoopCallbacks,
} from "../loop/MultiplayerGameLoop";
import { IInputSource } from "../../managers/input-source";
import { getGameMode } from "../../components/MultiPlayer/modes/GameModeBehavior";
import { AnnouncerManager } from "../managers/AnnouncerManager";
import { AnnouncerPackType } from "../../../core/engine/packs/announcers/AnnouncerPackFactory";
import { IAudioManager } from "../../../core/engine/types/audio-manager";

export type GameModeName =
  | "standard"
  | "survivor"
  | "sprint"
  | "hot_potato"
  | "team";

export interface GameRuntimeCallbacks extends GameLoopCallbacks {
  onViewReady?: () => void;
  onViewDisposed?: () => void;
  onGameMessage?: (message: GameMessageEvent) => void;
}

interface RuntimeParams {
  gameController: GameController;
  localGameManager: LocalGameManager;
  remoteGameManager: RemoteGameManager;
  inputSources: IInputSource[];
  getCanvasElement: () => HTMLCanvasElement | null;
  getCanvasSize: () => number;
}

export class GameRuntimeController {
  private readonly gameController: GameController;
  private readonly localGameManager: LocalGameManager;
  private readonly remoteGameManager: RemoteGameManager;
  private readonly inputSources: IInputSource[];
  private readonly getCanvasElement: () => HTMLCanvasElement | null;
  private readonly getCanvasSize: () => number;
  private readonly callbacks: GameRuntimeCallbacks;
  private announcerManager?: AnnouncerManager;
  private announcerAudioManager?: IAudioManager;

  private gameLoop: MultiplayerGameLoop | null = null;
  private disposed = false;

  constructor(params: RuntimeParams, callbacks: GameRuntimeCallbacks = {}) {
    this.gameController = params.gameController;
    this.localGameManager = params.localGameManager;
    this.remoteGameManager = params.remoteGameManager;
    this.inputSources = params.inputSources;
    this.getCanvasElement = params.getCanvasElement;
    this.getCanvasSize = params.getCanvasSize;
    this.callbacks = callbacks;

    this.gameController.setRemoteGameManager(this.remoteGameManager);

    // Announcer manager will be initialized once the audio manager is ready
  }

  setFragAnnouncerPack(type: AnnouncerPackType): void {
    const manager = this.ensureAnnouncerManager();
    manager?.setFragAnnouncerPack(type);
  }

  private ensureAnnouncerManager(): AnnouncerManager | null {
    const localGame = this.localGameManager.getLocalGame();
    const audioManager = localGame?.audioManager;
    if (!audioManager) {
      return this.announcerManager ?? null;
    }

    if (this.announcerManager && this.announcerAudioManager === audioManager) {
      return this.announcerManager;
    }

    this.announcerAudioManager = audioManager;
    const manager = new AnnouncerManager(audioManager);
    manager.setOnMessage((message) => {
      this.callbacks.onGameMessage?.(message);
    });
    this.announcerManager = manager;
    return manager;
  }

  handleGameState(gameState: GameState | null): void {
    if (this.disposed) {
      return;
    }

    if (!gameState) {
      this.stopGameLoop();
      return;
    }

    const resolvedMode = this.resolveMode(gameState.gameMode);

    if (
      !this.gameLoop &&
      resolvedMode &&
      this.shouldStartGameLoop(gameState, resolvedMode)
    ) {
      this.startGameLoop(gameState);
      // Initialize announcer manager after the game loop starts (game will be initialized)
      setTimeout(() => this.ensureAnnouncerManager(), 100);
    }

    if (this.gameLoop && gameState.status === "finished") {
      this.stopGameLoop();
      return;
    }

    if (this.gameLoop) {
      const modeBehavior = getGameMode(gameState.gameMode);
      this.gameLoop.updateStatus(
        gameState.status,
        gameState.countdown,
        modeBehavior,
        gameState.isPaused
      );
    }

    // Handle announcer messages
    const announcerManager = this.ensureAnnouncerManager();
    if (
      announcerManager &&
      gameState.gameMessages &&
      gameState.gameMessages.length > 0 &&
      gameState.localConnectionId
    ) {
      announcerManager.handleGameMessages(
        gameState.gameMessages,
        gameState,
        gameState.localConnectionId
      );
    }
  }

  resizeView(): void {
    if (!this.gameLoop) {
      return;
    }
    const canvasSize = this.getCanvasSize();
    this.gameLoop.resizeView(canvasSize);
  }

  dispose(): void {
    this.disposed = true;
    this.stopGameLoop();
  }

  private shouldStartGameLoop(
    gameState: GameState,
    mode: GameModeName
  ): boolean {
    if (!gameState) {
      return false;
    }
    const statusAllowsStart =
      gameState.status === "waiting" ||
      gameState.status === "starting" ||
      gameState.status === "active";
    if (!statusAllowsStart) {
      return false;
    }
    if (!gameState.localConnectionId || gameState.localConnectionId.length === 0) {
      return false;
    }
    if (gameState.gameSeed === undefined) {
      return false;
    }
    if (!mode) {
      return false;
    }
    return true;
  }

  private startGameLoop(gameState: GameState): void {
    const canvas = this.getCanvasElement();
    if (!canvas) {
      console.error("[GameRuntimeController] Canvas element not available");
      return;
    }

    const gameLoop = new MultiplayerGameLoop();
    const callbacks: GameLoopCallbacks = {
      onStatsUpdate: (stats) => {
        this.callbacks.onStatsUpdate?.(stats);
      },
      onCountdownChange: (countdown) => {
        this.callbacks.onCountdownChange?.(countdown);
      },
      onCountdownFinished: () => {
        this.callbacks.onCountdownFinished?.();
      },
      onGameInitialized: (game, view) => {
        this.callbacks.onGameInitialized?.(game, view);
        this.callbacks.onViewReady?.();
      },
    };

    gameLoop.initialize(
      canvas,
      this.getCanvasSize(),
      gameState.gameSeed,
      this.gameController,
      this.localGameManager,
      this.remoteGameManager,
      this.inputSources,
      callbacks
    );

    gameLoop.start();
    this.gameLoop = gameLoop;
  }

  private stopGameLoop(): void {
    if (this.gameLoop) {
      this.gameLoop.stop();
      this.gameLoop = null;
      this.callbacks.onViewDisposed?.();
    }
  }

  private resolveMode(mode?: string): GameModeName | undefined {
    if (
      mode === "standard" ||
      mode === "survivor" ||
      mode === "sprint" ||
      mode === "hot_potato" ||
      mode === "team"
    ) {
      return mode;
    }
    return undefined;
  }
}

